import { defineFeature } from '../define-feature';
import { TEST_IDS, fieldTestId, optionTestId, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'marketing.campaign.sms.create',
  category: 'marketing',
  title: 'Créer une campagne SMS',
  intent:
    'Créer et envoyer une campagne SMS à des clients ciblés : nom, destinataires, expéditeur, texte — avec un correcteur de caractères, le suivi des clics, un aperçu et une estimation du coût.',
  context: {
    why: "Le SMS a un taux de lecture bien plus élevé que l'e-mail : c'est le canal idéal pour une promo à durée limitée ou un rappel qui doit être vu. Sa contrepartie, c'est qu'il est payant, à l'unité. D'où trois garde-fous intégrés à la création : le correcteur, qui vous alerte sur les caractères spéciaux (accents, emojis) qui font gonfler le coût ; l'estimation du coût, affichée avant l'envoi ; et l'envoi test sur votre propre numéro, pour vérifier le rendu réel avant d'engager la dépense. Vous ciblez toute votre base ou un segment précis via les tags, et seuls les clients ayant accepté les SMS marketing sont contactés.",
    use_cases: [
      'Envoyer une promo flash à un segment de clients taggés.',
      'Rappeler un événement ou une date importante à vos clients.',
      'Diffuser un message court avec un lien dont vous suivez les clics.',
    ],
  },
  required_scopes: ['campaign.sms.create'],
  ui_entry_points: [
    { route: '/marketing/campaigns', element: 'Bouton « Créer une campagne + » → sous-action « SMS »' },
  ],
  api: {
    primary_operation_id: 'ProCampaignSmsAppController_createProCampaignSms',
    related_operation_ids: [
      'ProCampaignSmsAppController_putProCampaignSms',
      'ProCampaignAppController_getProCampaign',
      'SmsAppController_estimateSmsCampaignCost',
    ],
  },
  steps: [
    '**Cliquez sur « Créer une campagne »** puis choisissez « SMS ».',
    '**Nommez la campagne** pour la retrouver ensuite dans votre liste.',
    '**Choisissez les destinataires** : tous vos clients, ou un ou plusieurs tags pour cibler un segment.',
    "**Ajustez l'expéditeur** si besoin (il reprend par défaut le nom réglé dans les Paramètres).",
    "**Rédigez le texte** : le correcteur signale les caractères spéciaux qui augmentent le coût, et l'estimation s'affiche pendant que vous écrivez.",
    '**Activez le suivi des clics** si votre message contient un lien.',
    "**Vérifiez l'aperçu et le coût estimé**, faites un envoi test sur votre propre numéro, puis programmez ou envoyez.",
  ],
  gotchas: [
    "**Les caractères spéciaux coûtent plus cher.** Accents et emojis changent l'encodage du SMS et réduisent le nombre de caractères par message : le correcteur vous alerte pour que vous puissiez simplifier le texte et maîtriser le coût.",
    "**Les SMS sont facturés : vérifiez l'estimation avant d'envoyer.** Le coût dépend du nombre de destinataires et de la longueur du message. Les pays autorisés à l'envoi se règlent dans les Paramètres de communication.",
    "**Faites toujours un envoi test avant l'envoi réel.** Testez sur votre propre numéro pour vérifier le rendu, l'expéditeur et le lien : une fois envoyé à toute la base, on ne rattrape rien.",
    "**Seuls les clients ayant accepté les SMS marketing sont ciblés.** Un client qui a refusé n'est pas compté ni facturé, même s'il fait partie d'un tag visé — le consentement prime sur le ciblage.",
    "**Réglez d'abord vos Paramètres de communication.** L'expéditeur SMS et l'acceptation des tarifs se font une fois, dans les Paramètres, avant votre première campagne.",
  ],
  related_features: ['marketing.communication.settings', 'customers.tag.manage', 'marketing.campaign.email.schedule'],
  demo_script: {
    duration_target_s: 40,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Le SMS est lu presque à coup sûr : le canal parfait pour une promo qui doit être vue.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      { action: 'click', target: sidebarSubTestId('/marketing/campaigns'), caption: 'Puis « Campagne marketing ».' },
      { action: 'click', target: TEST_IDS.campaigns_create, caption: 'Créez une campagne.' },
      {
        action: 'click',
        target: optionTestId(TEST_IDS.campaigns_create, 1),
        caption: 'Choisissez le type SMS.',
        capture: true,
      },
      { action: 'wait_for', target: fieldTestId('name'), caption: 'Le formulaire de campagne SMS s’ouvre.' },
      {
        action: 'fill',
        target: fieldTestId('name'),
        value: 'Promo week-end',
        caption: 'Nommez la campagne pour la retrouver ensuite dans votre liste.',
      },
      {
        action: 'fill',
        target: fieldTestId('content'),
        value: 'Bonjour, -20% ce week-end sur toutes les prestations !',
        caption: 'Rédigez le texte : le correcteur signale les caractères spéciaux qui font grimper le coût.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.modal_submit,
        caption: 'On enregistre ; vous pourrez tester l’envoi sur votre propre numéro avant de l’envoyer à tous.',
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Vocal Kaltouma.) Campagne SMS : nom, destinataires (tags cochés), expéditeur (changeable), texte
// > + correcteur de caractères spéciaux (coûtent plus cher), suivi des clics, aperçu. On revient sur
// > les campagnes enregistrées : modifier, tester (envoi sur son numéro) avant l'envoi.
